import axios from "axios";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";

/**
 * Visitor inductions.
 *
 * These endpoints use the newer `{ success, data, message }` envelope, unlike
 * visitorInfo.service.js next door, which is on the legacy `{ code }` one — so
 * the two are kept in separate services rather than mixed.
 *
 * The `public*` calls send no auth header: they are the link a visitor opens on
 * their own phone, gated by the per-induction invite token instead.
 */
export const visitorInductionService = {
  getHistory,
  getAvailableForms,
  getSubmission,
  start,
  startForWorker,
  submit,
  verify,
  reject,
  invite,
  getPublic,
  submitPublicDetails,
  submitPublic,
  uploadPublicFile,
};

const unwrap = (res) => (res && res.status === 200 ? res.data : null);

function getHistory(visitorInfoId, limit = 5) {
  return axios
    .get(apiEndpoint.visitorInduction, {
      headers: authHeader(),
      params: { visitor_info_id: visitorInfoId, limit },
    })
    .then(unwrap);
}

function getAvailableForms(propertyId) {
  return axios
    .get(`${apiEndpoint.visitorInduction}/available-forms`, {
      headers: authHeader(),
      params: { property_id: propertyId },
    })
    .then(unwrap);
}

// The answers a completed induction recorded, with the schema snapshot taken at
// submission time so an old record renders its own field set.
function getSubmission(inductionId) {
  return axios
    .get(`${apiEndpoint.visitorInduction}/submission`, {
      headers: authHeader(),
      params: { id: inductionId },
    })
    .then(unwrap);
}

function start(visitorInfoId, formId) {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/start`,
      { visitor_info_id: visitorInfoId, form_id: formId },
      { headers: authHeader() }
    )
    .then(unwrap);
}

// Run an induction for a contractor's worker rather than against a visit — the
// worker may never have checked in, so there is no visitor row to attach to.
function startForWorker(workerId, formId) {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/start-for-worker`,
      { worker_id: workerId, form_id: formId },
      { headers: authHeader() }
    )
    .then(unwrap);
}

function submit(id, answers) {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/submit`,
      { id, answers },
      { headers: authHeader() }
    )
    .then(unwrap);
}

// Accept a submitted induction. Only an owner/admin on the property, or the
// person who ran it, may do this — the server decides, not the button.
function verify(id) {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/verify`,
      { id },
      { headers: authHeader() }
    )
    .then(unwrap);
}

// Refuse one. The reason is required: a rejected induction is evidence of what
// was turned down, and without a reason nobody can act on it.
function reject(id, reason) {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/reject`,
      { id, reason },
      { headers: authHeader() }
    )
    .then(unwrap);
}

function invite(id, channel = "email") {
  return axios
    .post(
      `${apiEndpoint.visitorInduction}/invite`,
      { id, channel },
      { headers: authHeader() }
    )
    .then(unwrap);
}

function getPublic(token) {
  return axios
    .get(`${apiEndpoint.visitorInduction}/public`, { params: { token } })
    .then(unwrap);
}

// The details step on the invite link. Writes to the visitor record, not to the
// induction answers — the same split check-in makes between checkin_info and
// the schema submission. No auth header: the invite token is the gate.
function submitPublicDetails(token, details) {
  return axios
    .post(`${apiEndpoint.visitorInduction}/public-details`, { token, ...details })
    .then(unwrap);
}

function submitPublic(token, answers) {
  return axios
    .post(`${apiEndpoint.visitorInduction}/public-submit`, { token, answers })
    .then(unwrap);
}

function uploadPublicFile(formData) {
  return axios
    .post(`${apiEndpoint.visitorInduction}/public-upload`, formData)
    .then(unwrap);
}
